import React, { useRef } from "react";
import { useFitText } from "../lib/useFitText";
import { bgStyle } from "../lib/bgStyle";

/**
 * Password (ticket code + desk + history) zone with self-adapting typography.
 *
 * - In `wideLayout` (vertical-stack layouts like PASSWORD_TOP / MEDIA_TOP), the
 *   ticket code and desk render side-by-side on the same baseline so the code
 *   can grow much larger horizontally.
 * - In regular layouts the code stacks above the desk row.
 * - History (last tickets) renders side-by-side in wide layout, below in normal.
 * - `compact` is for PiP corners (smaller paddings, no headers).
 *
 * Measurement strategy: each text element is measured against a *fixed-size*
 * parent ref to avoid ResizeObserver / inline-block measurement loops in
 * useFitText. See /app/frontend/src/lib/useFitText.js.
 */
export const PasswordZone = ({ state, colors, nomenclature, flashRef, compact = false, wideLayout = false, hideExtras = false }) => {
  const sectionRef = useRef(null);
  // Inline row used in wide layout for measuring code + desk on the same baseline.
  const inlineRowRef = useRef(null);

  const ticketCode = state.currentTicket?.code || "AGUARDE";
  const deskCode = state.currentTicket?.desk || "";

  // --- Regular (stacked) measurements: against the whole section.
  // When `hideExtras` is on (PiP big-password mode) we have no header/history,
  // so the code can take up much more vertical space.
  const ticketRefStacked = useFitText(ticketCode, {
    max: compact ? 140 : 900,
    min: compact ? 36 : 56,
    containerRef: sectionRef,
    maxHeightRatio: compact ? 0.55 : hideExtras ? 0.72 : 0.55,
  });
  const deskRefStacked = useFitText(deskCode, {
    max: compact ? 56 : hideExtras ? 280 : 200,
    min: compact ? 20 : 40,
    containerRef: sectionRef,
    maxHeightRatio: compact ? 0.18 : hideExtras ? 0.22 : 0.18,
  });

  // --- Wide-layout measurements: code + desk share the inline row.
  // No history side-panel anymore → code gets ~68%, desk number ~22% of the inline row width.
  // Height fills the row.
  const ticketRefWide = useFitText(ticketCode, {
    max: 900,
    min: 80,
    containerRef: inlineRowRef,
    maxHeightRatio: 1,
    widthRatio: 0.68,
  });
  const deskRefWide = useFitText(deskCode, {
    max: 420,
    min: 40,
    containerRef: inlineRowRef,
    maxHeightRatio: 0.8,
    widthRatio: 0.22,
  });

  const ticketRef = wideLayout ? ticketRefWide : ticketRefStacked;
  const deskRef = wideLayout ? deskRefWide : deskRefStacked;

  const padding = compact ? "p-4" : wideLayout ? "p-6 md:p-10" : "p-8 md:p-12 lg:p-16";
  const gap = compact ? "gap-3" : wideLayout ? "gap-4 md:gap-6" : "gap-8 md:gap-12";
  const labelSize = compact ? "text-[9px]" : "text-xs md:text-sm";
  const hasHistory = !compact && !hideExtras && (state.history?.length || 0) > 0;
  const showHistory = hasHistory;

  // Current ticket + desk block (stacked: code on top, desk row below)
  const CurrentTicketBlock = (
    <div ref={flashRef} className="w-full min-w-0 flex-1 flex flex-col justify-center gap-4 min-h-0">
      {state.currentTicket ? (
        <>
          <div className="w-full min-w-0 flex items-center">
            <div
              ref={ticketRef}
              className="font-heading font-black leading-none tracking-tighter whitespace-nowrap"
              style={{ color: colors.passwordAccent }}
              data-testid="current-ticket-code"
            >
              {state.currentTicket.code}
            </div>
          </div>
          <div className={`${compact ? "mt-1" : "mt-2 md:mt-4"} flex items-baseline gap-${compact ? "3" : "6"} flex-wrap shrink-0 w-full`}>
            {!compact && (
              <span className={`${labelSize} uppercase tracking-[0.3em] opacity-60`}>
                {nomenclature}
              </span>
            )}
            <span
              ref={deskRef}
              className="font-heading font-bold tracking-tighter whitespace-nowrap leading-none"
              data-testid="current-ticket-desk"
            >
              {state.currentTicket.desk}
            </span>
          </div>
        </>
      ) : (
        <div className="w-full min-w-0 flex items-center justify-start">
          <div
            ref={ticketRef}
            className="font-heading font-black leading-none tracking-tighter opacity-20 whitespace-nowrap"
            style={{ color: colors.passwordText }}
            data-testid="current-ticket-code"
          >
            AGUARDE
          </div>
        </div>
      )}
    </div>
  );

  // Wide-layout ticket: code + (nomenclature label above desk number) inline on the same row.
  // No history shown — full focus on the current ticket and its consultório.
  const WideTicketBlock = (
    <div ref={flashRef} className="w-full min-w-0 flex-1 flex flex-col justify-center min-h-0">
      <div
        ref={inlineRowRef}
        className="w-full min-w-0 flex-1 min-h-0 flex items-center justify-start gap-6 md:gap-12"
      >
        {state.currentTicket ? (
          <>
            <div
              ref={ticketRef}
              className="font-heading font-black leading-none tracking-tighter whitespace-nowrap shrink-0"
              style={{ color: colors.passwordAccent }}
              data-testid="current-ticket-code"
            >
              {state.currentTicket.code}
            </div>
            <div className="flex flex-col items-start justify-center min-w-0 shrink-0 gap-1 md:gap-2">
              <span
                className={`${labelSize} uppercase tracking-[0.3em] opacity-60 leading-none whitespace-nowrap`}
                data-testid="current-ticket-desk-label"
              >
                {nomenclature}
              </span>
              <span
                ref={deskRef}
                className="font-heading font-bold tracking-tighter whitespace-nowrap leading-none opacity-95"
                data-testid="current-ticket-desk"
              >
                {state.currentTicket.desk}
              </span>
            </div>
          </>
        ) : (
          <div
            ref={ticketRef}
            className="font-heading font-black leading-none tracking-tighter opacity-20 whitespace-nowrap"
            style={{ color: colors.passwordText }}
            data-testid="current-ticket-code"
          >
            AGUARDE
          </div>
        )}
      </div>
    </div>
  );

  // History block — plain JSX builder (not a sub-component, to avoid remount on parent re-renders)
  const historyEl = (vertical) =>
    hasHistory ? (
      <div
        className={`shrink-0 ${
          vertical
            ? "w-1/2 max-w-[40%] pl-6 md:pl-10 border-l self-stretch flex flex-col justify-center"
            : "w-full pt-6 md:pt-10 border-t"
        }`}
        style={{ borderColor: colors.passwordText + "20" }}
      >
        <div className={`${labelSize} uppercase tracking-[0.3em] opacity-60 mb-4 md:mb-6`}>Últimas Senhas</div>
        <div className={vertical ? "flex flex-col gap-3 md:gap-5" : "flex flex-wrap gap-4 md:gap-6"}>
          {state.history.slice(0, 4).map((h, i) => (
            <div
              key={i}
              className="flex items-baseline gap-2 md:gap-3"
              data-testid={`history-ticket-${i}`}
            >
              <span
                className="font-heading font-bold tracking-tighter"
                style={{
                  color: colors.historyAccent,
                  fontSize: vertical ? "clamp(28px, 4vw, 72px)" : "clamp(20px, 2.5vw, 56px)",
                  opacity: 1 - i * 0.15,
                }}
              >
                {h.code}
              </span>
              <span className="text-xs md:text-sm uppercase tracking-[0.2em] opacity-50">
                {(nomenclature || "G").charAt(0).toUpperCase()}.{h.desk}
              </span>
            </div>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <section
      ref={sectionRef}
      className={`relative flex flex-col justify-center items-start ${gap} ${padding} overflow-hidden transition-all duration-500 w-full h-full min-w-0`}
      style={{ ...bgStyle(colors.passwordBg, colors.passwordBgOpacity ?? 1, colors.passwordBgImage || ""), color: colors.passwordText }}
      data-testid="password-zone"
    >
      {!compact && !wideLayout && !hideExtras && (
        <div className="flex flex-col gap-2 shrink-0">
          <span className={`${labelSize} uppercase tracking-[0.3em] opacity-60`}>Senha Atual</span>
          <div className="flex items-end gap-3">
            <div className="w-3 h-3 mb-4 md:mb-6" style={{ backgroundColor: colors.passwordAccent }} />
            <span className="text-xs uppercase tracking-[0.2em] opacity-60 mb-4 md:mb-6">Chamando</span>
          </div>
        </div>
      )}

      {wideLayout ? (
        <div className="flex-1 w-full min-w-0 flex flex-col justify-center">
          {WideTicketBlock}
        </div>
      ) : (
        <>
          {CurrentTicketBlock}
          {showHistory && historyEl(false)}
        </>
      )}
    </section>
  );
};
